import { __, _n } from '@wordpress/i18n';
import { IFreemius_Checkout_Option, IFreemius_Purchase_Response } from 'classes/interface.freemius-checkout';
import { IFreemius_Google_Analytics_Settings, IGoogle_Analytics_Purchase_Data, IGoogle_Analytics_Purchase_Item_Data } from './interface.freemius-google-analytics'

/**
 * Implements the integration between Freemius Checkout and Google Analytics.
 *
 * @since 0.7.1.210423
 * @link https://freemius.com/help/documentation/selling-with-freemius/freemius-checkout-buy-button/#tracking_purchases_with_google_analytics_and_facebook
 * @link https://developers.google.com/analytics/devguides/collection/gtagjs/ecommerce
 */
export class Freemius_Google_Analytics {
	/**
	 * The settings passed to the class.
	 *
	 * @var IFreemius_Google_Analytics_Settings
	 */
	protected settings: IFreemius_Google_Analytics_Settings;

	/**
	 * Constructor.
	 *
	 * @param settings
	 */
	public constructor(settings: IFreemius_Google_Analytics_Settings) {
		this.settings = settings;
	}

	/**
	 * Sends the detail of a purchase to Google Analytics.
	 *
	 * Note
	 * This method relies on Google Analytics 4 and won't work with the legacy analytics.
	 *
	 * @param freemius_response The response returned by Freemius.
	 * @param freemius_checkout_option The purchased option.
	 * @return void
	 * @link https://developers.google.com/gtagjs/reference/ga4-events#purchase
	 */
	public track_purchase(freemius_response: IFreemius_Purchase_Response, freemius_checkout_option: IFreemius_Checkout_Option): void {
		// Safeguard. If the Google Tag script is not loaded, stop here
		if(!gtag) {
			return;
		}

		// Determine if the purchase is for a trial version
		const is_trial: boolean = ((freemius_response.purchase.trial_ends ?? false) !== false);

		let purchase_amount: number = 0;
		if(!is_trial) {
			purchase_amount = freemius_response.purchase.initial_amount ?? freemius_response.purchase.gross;
		}

		// @link https://developers.google.com/tag-platform/gtagjs/reference/events#purchase
		const purchase_event_data: IGoogle_Analytics_Purchase_Data = {
			transaction_id: freemius_response.purchase.id,
			affiliation: this.settings.store.name,
			value: purchase_amount,
			currency: freemius_response.purchase.currency.toUpperCase(),
			tax: freemius_response.purchase.tax ?? 0,
			coupon: freemius_response.purchase.coupon_id ?? '',
			// Add the details of the item
			// @since 0.8.6.211023
			// @link https://developers.google.com/tag-platform/gtagjs/reference/events#purchase_item
			items: [
				{
					// Use the product SKU returned by WooCommerce, if set and not empty. If empty, take the plugin ID from Freemius
					item_id: freemius_checkout_option.product_sku || freemius_response.purchase.plugin_id,
					// Pass the product title from WooCommerce as the product name
					item_name: freemius_checkout_option.product_title,
					// Freemius allow to buy one product at a time. Due to that, the price and currency and coupon are the
					// same as the main transaction
					price: purchase_amount,
					currency: freemius_response.purchase.currency.toUpperCase(),
				}
			]
		}

		// Send the purchase event to Google Analytics
		gtag('event', 'purchase', purchase_event_data);
	}
}